import { useState, useEffect } from "react";
import { useTemplateStore } from "@/zustand/templateStore.js";
import { useProductData } from "@/zustand/productStore.js";
import { useCartData } from "@/zustand/cartStore.js";
import { useMarketingStore } from "@/zustand/emailMarketingStore.js";
import { ChevronDownIcon, QuestionMarkIcon } from "@/utils/miscIcons.jsx";
import { useOrderCalculations } from "@/func/orderCalculations.js";
import ProductsList from "@/components/OrderSummary/ProductsList";
import DiscountInput from "@/components/Inputs/DiscountInput";
import Separator from "@/components/Misc/Separator";
import ExtendedWarrantyCheckbox from "@/components/OrderSummary/ExtendedWarrantyCheckbox";
import TotalSavings from "@/components/OrderSummary/TotalSavings";
import VideoImageContainer from "@/components/Misc/VideoImageContainer";
import OverViewDrawerImageFallback from "../../assets/images/overviewDrawerImage.jpg";
import s from "@/components/OrderSummary/OrderSummary.module.scss";
import cn from "@/utils/classNames.js";

// The shipping policy link may point at an external mid-domain (domain
// replacer); open it in a new tab while keeping same-host links in place.
const opensInNewTab = (url) => {
  try {
    return new URL(url, window.location.href).host !== window.location.host;
  } catch {
    return false;
  }
};

const OrderSummary = ({ isMobile }) => {
  const base = window.post_template;
  const productData = useProductData();
  const { toggleOrderSummary, orderSummaryOpen, displayPopup, totalSavingsStyle } = useTemplateStore();
  const [warrantyEnabled, setWarrantyEnabled] = useState(null);
  const { selectedProduct, warrantyState, isDiscountApplied, setCartTotal } = useCartData();
  const summary = base?.checkout_v6_summary;
  const shippingPolicyUrl = summary.checkout_v6_summary_shippingLink;
  const MultiplyWarranty = productData?.multiply_price_by_amount;
  const { freeShippingApplied } = useMarketingStore();

  const orderSummaryMediaType = base?.order_overview_media_type;
  const orderSummaryImage = base?.order_overview_image;
  const orderSummaryVideo = base?.order_overview_r2_bucket_video_url;
  const orderSummaryVersion = summary?.checkout_v6_summary_version;

  const orderCalculations = useOrderCalculations(
    selectedProduct,
    isDiscountApplied,
    MultiplyWarranty,
    warrantyState,
    productData.formatted_string
  );

  useEffect(() => {
    setCartTotal(orderCalculations?.total_numeric);
  }, [orderCalculations?.total]);

  useEffect(() => {
    setWarrantyEnabled(productData?.add_warranty_for_product);
  }, [productData?.add_warranty_for_product]);

  const freeShippingLabel = freeShippingApplied
    ? summary.tr_checkout_v6_summary_free_shipping_applied_label
    : summary.tr_checkout_v6_summary_free_shipping_expired_label;

  return (
    <>
      {!isMobile && (
        <VideoImageContainer
          mediaType={orderSummaryMediaType}
          videoUrl={orderSummaryVideo}
          image={orderSummaryImage || OverViewDrawerImageFallback}
        />
      )}
      <div className={s.orderSummary}>
        {orderSummaryVersion ? null : (
          <div
            data-cy="cc-order-summary-container"
            className={cn(s.orderSummary__topCont, orderSummaryOpen && s["orderSummary__topCont--hide"])}
          >
            <div className={s["orderSummary__savingsLabel--cont"]}>
              <div className={s.orderSummary__savingsLabel}>{summary.tr_checkout_v6_summary_savingsLabel}</div>
              <div className={s.orderSummary__savingsLabel}>{orderCalculations?.savings}</div>
            </div>
            {freeShippingApplied && (
              <div className={s.orderSummary__freeShippingLabelTop}>
                {summary.tr_checkout_v6_summary_free_shipping_applied_label}
              </div>
            )}
            <Separator />
          </div>
        )}
        <div
          data-cy="cc-order-review-opener"
          className={cn(s.orderSummary__label, s.orderSummary__opener, orderSummaryOpen && s.chevronDown)}
          onClick={() => {
            toggleOrderSummary(!orderSummaryOpen);
          }}
        >
          {orderSummaryVersion ? (
            <div className={cn(s["orderSummary__label--title"], s["orderSummary__version2"])}>
              <span style={{ marginRight: "8px" }}>
                {base.checkout_v6_summary.tr_checkout_v6_order_review_accordion_title}
              </span>{" "}
              <ChevronDownIcon />
            </div>
          ) : (
            <>
              <div className={s["orderSummary__label--title"]}>
                <span style={{ marginRight: "8px" }}>
                  {base.checkout_v6_summary.tr_checkout_v6_order_review_accordion_title}
                </span>{" "}
                <ChevronDownIcon />
              </div>
              <div className={s["orderSummary__label--priceContainer"]}>
                <div className={s["orderSummary__label--price"]}>{orderCalculations?.total}</div>
              </div>
            </>
          )}
        </div>

        <div
          style={displayPopup ? { overflow: "visible" } : {}}
          className={cn(s.orderSummary__content, orderSummaryOpen && s["orderSummary__content--show"])}
        >
          <Separator margin="16px 0px 16px 0px" />
          <ProductsList />
          {warrantyEnabled && (
            <>
              <Separator margin="16px 0px 16px 0px" />
              <ExtendedWarrantyCheckbox />
            </>
          )}
          <Separator margin="16px 0px 0px 0px" />
          {productData.enable_discount_codes && (
            <>
              <DiscountInput /> <Separator margin="0px 0px 0px 0px" />
            </>
          )}
          <div className={s.orderTotals__container} style={{ marginBottom: "0px" }}>
            <div className={s.orderTotals}>
              <div className={s.orderTotals__item}>
                <p>{base?.checkout_v6_summary.tr_checkout_v6_summary_orderLabel}</p>
                <p data-cy="summary-subtotal">{orderCalculations?.subtotal}</p>
              </div>
              <div className={s.orderTotals__item}>
                <p data-testid="summary-shipping-label" className={s["orderTotals__item--shippingLabel"]}>
                  <span style={{ marginRight: "4px" }}>
                    {base.checkout_v6_summary.tr_checkout_v6_summary_shippingLabel}
                  </span>{" "}
                  <a
                    className={s["orderTotals__item--shippingLabelLink"]}
                    href={shippingPolicyUrl}
                    {...(opensInNewTab(shippingPolicyUrl) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  >
                    <QuestionMarkIcon />
                  </a>
                </p>
                <p data-cy="summary-shipping">{orderCalculations?.shipping}</p>
              </div>
              {freeShippingApplied && (
                <div className={cn(s.orderSummary__freeShippingLabelBottom, !freeShippingApplied && expired)}>
                  {freeShippingLabel}
                </div>
              )}
            </div>
            <Separator />
            <TotalSavings
              type={totalSavingsStyle}
              label={base?.checkout_v6_summary?.tr_checkout_v6_summary_savingsLabel}
              savings={orderCalculations?.savings}
            />
            <div className={s.total}>
              <div>{base?.checkout_v6_summary.tr_checkout_v6_summary_totalLabel}</div>
              <div data-cy="summary-total">{orderCalculations?.total}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderSummary;
